import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useLocale, useT } from '../i18n/locale-context';
import { fetchSemanticSearch } from '../lib/catalog-client';
import { pluralRu } from '../lib/format';
import { SaveSearchButton } from './save-search-button';
import { BellIcon } from './icons';

interface NoResultsAlertProps {
  /** The keyword that returned nothing — the whole reason this is shown. */
  q: string;
  region?: string;
  category?: string;
  ikpu?: string;
  nicheSlug?: string;
  scope?: string;
  /** The status axis the empty list was asked under (`active`, `all`, …). */
  status?: string;
  /**
   * The filters that do NOT narrow in the `catalog-narrowing` sense, so
   * `hrefWithoutNarrowing` keeps them and the search endpoint still applies
   * them on the page it opens: the buyer pin, the exclude words, the budget
   * bounds, and `/results`' route-imposed `awarded`. The «whole catalogue»
   * count is asked under them too, or it would promise lots the link then
   * does not show. Same values the page's own search request carries.
   */
  customer?: string;
  exclude?: string;
  minBudget?: number;
  maxBudget?: number;
  awarded?: boolean;
  /**
   * The same page (locale prefix included) with every narrowing filter
   * dropped — `hrefWithoutNarrowing` in `lib/catalog-narrowing` — or absent
   * when nothing narrowed the search. Present → the card also says what the
   * WHOLE catalogue holds for the word, with a link there.
   */
  hrefWithoutNarrowing?: string | null;
}

/**
 * The empty state for a keyword search that matched nothing.
 *
 * ## Why this is not just a nicer "no results" message
 *
 * A search returning nothing is usually not a broken search — the catalogue
 * holds ~2 400 live lots at a time, and a real, specific demand simply is not
 * being tendered TODAY. Measured on the 107-query relevance set, 14 queries
 * match fewer than five live lots, and for several of them (`тостер`,
 * `Фальшпол`, `bitonlash`) no relevant lot exists at any depth. Every ranking
 * lever was tried against those queries and none of them can help: there is
 * nothing to rank.
 *
 * What the user actually wants is to be told WHEN it appears — which is the
 * product's own paid loop, and until now the one moment we were guaranteed to
 * waste. An empty page is the point of highest intent and lowest cost to ask:
 * they have just typed exactly what they want, and there is nothing else on
 * screen competing for the click.
 *
 * The button is the ordinary {@link SaveSearchButton}, not a special path, so
 * signed-out users get the same stash-and-redirect flow, the free-tier
 * saved-search cap still applies, and the alert is created by the same code
 * that creates every other one. `matchCount={0}` is passed honestly — it makes
 * the button show its own "these filters match nothing right now" warning,
 * which here is the entire premise rather than a caveat.
 *
 * ## The «without the filter» line (2026-10-20)
 *
 * Two thirds of empty keyword searches ran under a filter the person had
 * picked earlier (a category from the home-page picker stays in the URL while
 * they type the next word): «бумага» under «Медицина» finds 0 while the whole
 * catalogue holds 116. With a narrowing filter 29.9 % of searches came back
 * empty, without one 6.8 %, and 44 % of those people left without opening a
 * lot. So when a filter narrows the search, the card asks the API what the
 * unfiltered catalogue has for the same word (one request, `limit=1`, the
 * `total` is all it needs) and, if anything, says so with a link. The filter
 * is never dropped silently — the person clicks. See `lib/catalog-narrowing`.
 *
 * The count must be the number the link opens: the request drops exactly what
 * the link drops and keeps exactly what the search endpoint still applies on
 * that page (buyer, exclude words, budget bounds, `awarded`). The deadline
 * bounds and `sort` stay in the link too, but the page's keyword search does
 * not send them (catalog.tsx), so they are not sent here either.
 */
export function NoResultsAlert({
  q,
  region,
  category,
  ikpu,
  nicheSlug,
  scope,
  status,
  customer,
  exclude,
  minBudget,
  maxBudget,
  awarded,
  hrefWithoutNarrowing,
}: NoResultsAlertProps) {
  const t = useT();
  const { locale } = useLocale();
  const { data: unfiltered } = useQuery({
    queryKey: [
      'catalog',
      'search',
      'unnarrowed',
      { q, status, scope, customer, exclude, minBudget, maxBudget, awarded },
      locale,
    ],
    queryFn: () =>
      fetchSemanticSearch({
        q,
        status,
        scope,
        customer,
        exclude,
        minBudget,
        maxBudget,
        awarded,
        offset: 0,
        limit: 1,
        lang: locale,
      }),
    // A blank word is not a search (the page lists the catalogue then), so
    // there is no «same word without the filter» to count.
    enabled: Boolean(hrefWithoutNarrowing) && q.trim() !== '',
    staleTime: 5 * 60 * 1000,
  });
  const elsewhere =
    hrefWithoutNarrowing && unfiltered && unfiltered.total > 0 ? unfiltered.total : 0;
  return (
    <div className="rounded-lg border border-edge bg-bone-surface p-8 text-center shadow-m-sm">
      <BellIcon className="mx-auto h-6 w-6 text-ink-muted" />
      <p className="mt-3 text-[15px] font-medium text-ink">{t('search.empty.title')}</p>
      <p className="mx-auto mt-1 max-w-md text-[13px] leading-relaxed text-ink-muted">
        {t('search.empty.body')}
      </p>
      {elsewhere > 0 && hrefWithoutNarrowing && (
        <p
          data-testid="empty-without-filter"
          className="mx-auto mt-4 max-w-md rounded-md border border-brand/30 bg-brand/5 px-4 py-3 text-[14px] leading-relaxed text-ink"
        >
          {t('search.empty.withoutFilter', {
            q,
            count: elsewhere,
            word: pluralRu(elsewhere, 'лот', 'лота', 'лотов'),
          })}{' '}
          <Link to={hrefWithoutNarrowing} className="font-semibold text-brand underline">
            {t('search.empty.withoutFilterLink')}
          </Link>
        </p>
      )}
      <div className="mt-4 flex justify-center">
        <SaveSearchButton
          q={q}
          region={region}
          category={category}
          ikpu={ikpu}
          nicheSlug={nicheSlug}
          scope={scope}
          highlight
          matchCount={0}
        />
      </div>
    </div>
  );
}
